import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorAppPlugin, type URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

export type AppType = 'pro' | 'couple' | 'web';

const APP_TYPE_STORAGE_KEY = 'capacitor_app_type';

const isAppType = (value: unknown): value is Exclude<AppType, 'web'> =>
  value === 'pro' || value === 'couple';

const readStored = (store: Storage | undefined): AppType | null => {
  try {
    const stored = store?.getItem(APP_TYPE_STORAGE_KEY);
    return isAppType(stored) ? stored : null;
  } catch {
    return null;
  }
};

const persistAppType = (value: Exclude<AppType, 'web'>) => {
  // localStorage first: it survives a WebView process restart and the round
  // trip through the system browser during Google / Apple sign-in. Without it
  // the Pro app silently "becomes" the Couple app after logging in.
  try {
    window.localStorage.setItem(APP_TYPE_STORAGE_KEY, value);
  } catch {
    // private mode / storage disabled - sessionStorage below is the fallback
  }
  try {
    window.sessionStorage.setItem(APP_TYPE_STORAGE_KEY, value);
  } catch {
    // ignore
  }
};

/**
 * The native bundle identifier is the only fully trustworthy signal of which
 * app this WebView belongs to - it cannot be changed by navigation, a stale
 * storage value or a deep link. Populated asynchronously on launch.
 */
let bundleAppType: AppType | null = null;

/**
 * Determine which native app this WebView belongs to.
 * Priority:
 *   1. Native bundle id (com.everafterlist.pro / .couple) - authoritative
 *   2. ?appType=... query string (set by capacitor.config server.url on first launch)
 *   3. Persisted value in localStorage, then sessionStorage
 *   4. Path-based fallback (/pro/* => pro, otherwise couple)
 *   5. 'web' when not running natively
 */
export const getAppType = (): AppType => {
  if (!Capacitor.isNativePlatform()) return 'web';

  if (isAppType(bundleAppType)) return bundleAppType;

  const urlParams = new URLSearchParams(window.location.search);
  const appTypeParam = urlParams.get('appType');

  if (isAppType(appTypeParam)) {
    persistAppType(appTypeParam);
    return appTypeParam;
  }

  const stored = readStored(window.localStorage) ?? readStored(window.sessionStorage);
  if (stored) return stored;

  // Last-resort path-based detection
  return window.location.pathname.startsWith('/pro') ? 'pro' : 'couple';
};

/**
 * Resolve the app identity from the native bundle id once per launch and pin
 * it. Also repairs a contaminated storage value (e.g. a Couple value left by a
 * shared WebView data store) so role boundaries can never be crossed.
 */
export const resolveAppTypeFromBundle = async (): Promise<AppType> => {
  if (!Capacitor.isNativePlatform()) return 'web';
  try {
    const info = await CapacitorAppPlugin.getInfo();
    const id = info?.id ?? '';
    if (id === 'com.everafterlist.pro') bundleAppType = 'pro';
    else if (id === 'com.everafterlist.couple' || id === 'com.everafterlist.app') bundleAppType = 'couple';
    if (isAppType(bundleAppType)) {
      persistAppType(bundleAppType);
      return bundleAppType;
    }
  } catch {
    // getInfo unavailable - fall back to the query/storage chain
  }
  return getAppType();
};

/**
 * Routes that BOTH apps may legitimately render (auth flows, OAuth callbacks,
 * password reset, public legal pages). Anything outside these AND outside the
 * app's own namespace gets redirected back into the app's namespace.
 */
const SHARED_ALLOWED_PREFIXES = [
  '/auth',
  '/reset-password',
  '/privacy',
  '/privacy-policy',
  '/cookie-policy',
  '/terms',
  '/terms-of-service',
  '/unsubscribe',
  '/help',
  '/contact',
  '/data-deletion',
];

const isSharedAllowedPath = (pathname: string) =>
  SHARED_ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

export const useCapacitorApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNative = Capacitor.isNativePlatform();
  const [resolved, setResolved] = useState<AppType>(() => getAppType());
  const appType = resolved;

  // Pin the identity to the native bundle id on launch.
  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    resolveAppTypeFromBundle().then((value) => {
      if (!cancelled) setResolved(value);
    });
    return () => {
      cancelled = true;
    };
  }, [isNative]);


  useEffect(() => {
    if (!isNative) return;
    if (appType === 'web') return;

    const path = location.pathname;

    if (appType === 'pro') {
      if (path === '/business/login') {
        navigate('/pro/login', { replace: true });
        return;
      }
      // Pro app should ONLY ever show /pro/* (plus shared auth/legal pages).
      // If we somehow drift into /couple/*, /supplier/*, /venue/*, or root,
      // snap back into the Pro app immediately.
      // /supplier/* and /venue/* host the business editing screens the Pro
      // menus link to (profile edit, settings, account deletion, public preview).
      const isInProApp =
        path === '/pro' ||
        path.startsWith('/pro/') ||
        path.startsWith('/supplier/') ||
        path.startsWith('/venue/');
      if (!isInProApp && !isSharedAllowedPath(path)) {
        navigate('/pro/home', { replace: true });
      }
      return;
    }

    if (appType === 'couple') {
      // Couple app should never wander into /pro/*, /supplier/*, /venue/*,
      // /admin/*, or /business/*.
      const isCoupleArea =
        path === '/' ||
        path.startsWith('/couple/') ||
        path.startsWith('/directory') ||
        path.startsWith('/inspiration') ||
        path.startsWith('/blog') ||
        path.startsWith('/messages') ||
        path.startsWith('/share/') ||
        path.startsWith('/wedding/') ||
        path.startsWith('/real-wedding/') ||
        path.startsWith('/supplier/'); // public supplier profiles are fine to browse
      if (!isCoupleArea && !isSharedAllowedPath(path)) {
        navigate('/couple/dashboard', { replace: true });
      }
      return;
    }
  }, [isNative, appType, location.pathname, navigate]);

  // Universal / deep link handler: when the OS opens the app via a link
  // (email notification, tapped web URL, custom scheme), route to the
  // in-app path. Links look like:
  //   https://everafterlist.com/pro/leads/123    -> /pro/leads/123 in Pro app
  //   https://everafterlist.com/couple/checklist -> /couple/checklist in Couple
  //   everafterlist://pro/leads/123              -> custom scheme fallback
  useEffect(() => {
    if (!isNative) return;

    let cleanup: (() => void) | undefined;
    let disposed = false;

    CapacitorAppPlugin.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      try {
        // The provider returned a single-use code bound to this app's verifier.
        // Never treat the callback as ordinary navigation; the
        // sign-in sheet is closed once the session has been checked and applied.
        if (isNativeAuthReturn(event.url)) {
          void finishNativeSignIn(event.url, appType, navigate);
          return;
        }

        Browser.close().catch(() => undefined);

        const url = new URL(event.url);
        // Support both https universal links and the everafterlist:// scheme
        const rawPath = url.pathname + url.search + url.hash;
        const path = rawPath.startsWith('/') ? rawPath : `/${url.host}${rawPath}`;
        if (!path || path === '/') return;

        // Guard: don't let a Couple-app deep link drop us into /pro/* or vice versa.
        if (
          appType === 'pro' &&
          !path.startsWith('/pro') &&
          !path.startsWith('/supplier/') &&
          !path.startsWith('/venue/') &&
          !isSharedAllowedPath(path)
        ) {
          return;
        }
        if (appType === 'couple' && path.startsWith('/pro')) {
          return;
        }

        navigate(path, { replace: false });
      } catch (err) {
        console.warn('[deep-link] failed to parse url', event.url, err);
      }
    }).then((handle) => {
      if (disposed) { void handle.remove(); return; }
      cleanup = () => handle.remove();
      // A provider callback may cold-start the process instead of raising appUrlOpen.
      void CapacitorAppPlugin.getLaunchUrl?.().then((launch) => {
        if (!disposed && launch?.url && isNativeAuthReturn(launch.url)) {
          void finishNativeSignIn(launch.url, appType, navigate);
        }
      }).catch(() => undefined);
    });

    return () => { disposed = true; cleanup?.(); };
  }, [isNative, appType, navigate]);

  return { appType, isNative };
};

/** The app-scheme URL the native sign-in callback page opens (no network, no parsing of tokens here). */
export const isNativeAuthReturn = (rawUrl: string) => {
  try {
    const url = new URL(rawUrl);
    if (!['everafterlist:', 'everafterlistpro:'].includes(url.protocol) || url.username || url.password || url.port) return false;
    return `/${url.host}${url.pathname}` === '/auth/native-return';
  } catch {
    return false;
  }
};

/**
 * Apply a sign-in handed back from the system browser, then return to this
 * app's own sign-in screen, which routes by role. Loaded lazily so the web
 * bundle and unit tests don't pull in the native flow.
 */
export const finishNativeSignIn = async (
  rawUrl: string,
  appType: AppType,
  navigate: (to: string, opts?: { replace?: boolean }) => void,
) => {
  const [{ completeNativePkceOAuth }, { toast }] = await Promise.all([
    import('@/lib/nativePkceOAuth'),
    import('sonner'),
  ]);
  if (appType === 'web') return;
  const outcome = await completeNativePkceOAuth(rawUrl, appType);
  if (!outcome) return;
  if (outcome.status === 'failed') {
    toast.error(outcome.title, { description: outcome.message });
    return;
  }
  navigate(outcome.returnPath, { replace: true });
};
